import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import {
  getMembers,
  getMemberPointRecords,
  createPointRecord,
  updatePointRecord,
  deletePointRecord,
  saveMember,
  deleteMember,
  bulkUploadMembers,
  getPointApplications,
  createPointApplication,
  approvePointApplication,
  rejectPointApplication,
  deletePointApplication,
  deleteMembersWithoutPoints,
  POINT_CATEGORIES
} from '../firebase/pointService';
import { getUserByEmail } from '../firebase/userService';
import { canEditPointTracker, isAdmin } from '../utils/permissions';
import './PointTracker.css';

function PointTracker() {
  const navigate = useNavigate();
  const { currentUser } = useAuth();
  const [members, setMembers] = useState([]);
  const [selectedMember, setSelectedMember] = useState(null);
  const [memberRecords, setMemberRecords] = useState([]);
  const [applications, setApplications] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('records'); // Default to records for all users
  const [userPermissions, setUserPermissions] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');

  // Ref for scroll position restoration
  const leaderboardListRef = useRef(null);
  const scrollPositionRef = useRef(0);

  // Modals
  const [showMemberModal, setShowMemberModal] = useState(false);
  const [showPointModal, setShowPointModal] = useState(false);
  const [showApplicationModal, setShowApplicationModal] = useState(false);
  const [showMemberDetailModal, setShowMemberDetailModal] = useState(false);
  const [showCsvUploadModal, setShowCsvUploadModal] = useState(false);
  const [selectedMemberDetails, setSelectedMemberDetails] = useState(null);
  const [showPointHistoryModal, setShowPointHistoryModal] = useState(false);

  // Forms
  const [memberForm, setMemberForm] = useState({
    firstName: '',
    lastName: '',
    email: '',
    grade: '',
    category: '',
    studentId: ''
  });
  const [editingMember, setEditingMember] = useState(null);
  const [csvFile, setCsvFile] = useState(null);
  const [csvUploadStatus, setCsvUploadStatus] = useState(null);

  const [pointForm, setPointForm] = useState({
    category: 'general-meeting',
    points: 5,
    notes: '',
    eventName: '',
    eventDate: '',
    hours: '',
    rounds: '',
    placementOption: ''
  });
  const [editingPoint, setEditingPoint] = useState(null);

  const [applicationForm, setApplicationForm] = useState({
    memberName: '',
    studentId: '',
    category: 'tournament',
    notes: '',
    hours: '',
    rounds: '',
    placementOption: '',
    eventName: '',
    eventDate: ''
  });

  useEffect(() => {
    loadMembers();
    loadApplications();
    if (currentUser) {
      loadUserPermissions();
    } else {
      // Public access - no permissions
      setUserPermissions(null);
    }
  }, [currentUser]);

  // Restore scroll position after members update
  useEffect(() => {
    if (leaderboardListRef.current && scrollPositionRef.current > 0) {
      leaderboardListRef.current.scrollTop = scrollPositionRef.current;
      // Reset after restoration
      scrollPositionRef.current = 0;
    }
  }, [members]);

  const loadUserPermissions = async () => {
    if (!currentUser) {
      setUserPermissions(null);
      return;
    }

    // Get full user data
    const result = await getUserByEmail(currentUser.email);
    if (result.success && result.user) {
      const userData = result.user;
      setUserPermissions({
        ...userData.permissions,
        isAdmin: isAdmin(userData),
        canEdit: canEditPointTracker(userData),
        userData: userData
      });
    } else {
      // User not in system yet - default to view only
      setUserPermissions({ canEdit: false, isAdmin: false });
    }
  };

  const loadMembers = async () => {
    setLoading(true);
    const result = await getMembers();
    if (result.success) {
      setMembers(result.members);
    }
    setLoading(false);
  };

  const loadMemberRecords = async (memberId) => {
    const result = await getMemberPointRecords(memberId);
    if (result.success) {
      setMemberRecords(result.records);
    }
  };

  const loadApplications = async () => {
    const result = await getPointApplications();
    if (result.success) {
      setApplications(result.applications);
    }
  };

  const handleSelectMember = async (member) => {
    setSelectedMember(member);
    await loadMemberRecords(member.id);

    // Show modal for all users when clicking on leaderboard
    setShowPointHistoryModal(true);
  };

  const handleAddMember = () => {
    setEditingMember(null);
    setMemberForm({
      firstName: '',
      lastName: '',
      email: '',
      grade: '',
      category: '',
      studentId: ''
    });
    setShowMemberModal(true);
  };

  const handleEditMember = (member) => {
    setEditingMember(member);
    setMemberForm({
      firstName: member.firstName || '',
      lastName: member.lastName || '',
      email: member.email || '',
      grade: member.grade || '',
      category: member.category || '',
      studentId: member.studentId || ''
    });
    setShowMemberModal(true);
  };

  const handleSubmitMember = async (e) => {
    e.preventDefault();

    if (!memberForm.firstName || !memberForm.lastName) {
      alert('Please enter first and last name');
      return;
    }

    const result = await saveMember({
      ...memberForm,
      id: editingMember?.id
    });

    if (result.success) {
      setShowMemberModal(false);
      await loadMembers();
      if (selectedMember && editingMember && editingMember.id === selectedMember.id) {
        setSelectedMember({ ...selectedMember, ...memberForm });
      }
    } else {
      alert('Error saving member');
    }
  };

  const handleCsvUpload = () => {
    setShowCsvUploadModal(true);
    setCsvFile(null);
    setCsvUploadStatus(null);
  };

  const handleCsvFileChange = (e) => {
    const file = e.target.files[0];
    setCsvFile(file);
    setCsvUploadStatus(null);
  };

  const parseCsvFile = (file) => {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = (e) => {
        try {
          const text = e.target.result;
          const lines = text.split('\n').map(line => line.trim()).filter(line => line);

          // Skip header row
          const dataLines = lines.slice(1);

          const members = dataLines.map(line => {
            // Handle CSV with quoted fields
            const values = line.split(',').map(val => val.trim().replace(/^"|"$/g, ''));

            return {
              firstName: values[0] || '',
              lastName: values[1] || '',
              grade: values[2] || '',
              category: values[3] || '', // Varsity/Novice
              email: values[4] || '',
              studentId: values[5] || ''
            };
          }).filter(member => member.firstName && member.lastName);

          resolve(members);
        } catch (error) {
          reject(error);
        }
      };
      reader.onerror = reject;
      reader.readAsText(file);
    });
  };

  const handleSubmitCsv = async (e) => {
    e.preventDefault();

    if (!csvFile) {
      alert('Please select a CSV file');
      return;
    }

    try {
      setCsvUploadStatus({ status: 'loading', message: 'Processing CSV file...' });

      const membersData = await parseCsvFile(csvFile);

      if (membersData.length === 0) {
        setCsvUploadStatus({ status: 'error', message: 'No valid member data found in CSV' });
        return;
      }

      setCsvUploadStatus({ status: 'loading', message: `Uploading ${membersData.length} members...` });

      const result = await bulkUploadMembers(membersData);

      if (result.success) {
        setCsvUploadStatus({
          status: 'success',
          message: `Successfully added ${result.results.success} members${result.results.failed > 0 ? `, ${result.results.failed} failed` : ''}`,
          errors: result.results.errors
        });
        await loadMembers();
      } else {
        setCsvUploadStatus({ status: 'error', message: 'Error uploading members' });
      }
    } catch (error) {
      setCsvUploadStatus({ status: 'error', message: `Error: ${error.message}` });
    }
  };

  const handleViewMemberDetails = (member) => {
    // Only Mr. Willeby can view sensitive details
    const isMrWilleby = currentUser?.email?.toLowerCase() === 'kaseywilleby@gmail.com' ||
                        currentUser?.email?.toLowerCase() === 'mr.willeby@gmail.com';

    if (isMrWilleby) {
      setSelectedMemberDetails(member);
      setShowMemberDetailModal(true);
    }
  };

  const handleDeleteMember = async (memberId) => {
    if (window.confirm('Are you sure you want to delete this member? This cannot be undone.')) {
      const result = await deleteMember(memberId);
      if (result.success) {
        await loadMembers();
        if (selectedMember && selectedMember.id === memberId) {
          setSelectedMember(null);
          setMemberRecords([]);
        }
      } else {
        alert('Error deleting member');
      }
    }
  };

  const handleCleanupMembers = async () => {
    if (window.confirm('This will delete all members who have no point records. This action cannot be undone. Continue?')) {
      const result = await deleteMembersWithoutPoints();
      if (result.success) {
        alert(`Successfully deleted ${result.deletedCount} member(s) without points.`);
        await loadMembers();
        if (selectedMember && result.deletedMembers.includes(selectedMember.name)) {
          setSelectedMember(null);
          setMemberRecords([]);
        }
      } else {
        alert('Error cleaning up members');
      }
    }
  };

  const handleCategoryChange = (categoryValue) => {
    const category = POINT_CATEGORIES.find(c => c.value === categoryValue);
    if (!category) return;

    setPointForm({
      ...pointForm,
      category: categoryValue,
      points: category.defaultPoints,
      hours: '',
      rounds: '',
      placementOption: ''
    });
  };

  const handleAddPoints = (member) => {
    setSelectedMember(member);
    setEditingPoint(null);
    setPointForm({
      category: 'general-meeting',
      points: 5,
      notes: '',
      eventName: '',
      eventDate: '',
      hours: '',
      rounds: '',
      placementOption: ''
    });
    setShowPointModal(true);
  };

  const handleEditPoint = (record) => {
    setEditingPoint(record);
    setPointForm({
      category: record.category,
      points: record.points,
      notes: record.notes || '',
      eventName: record.eventName || '',
      eventDate: record.eventDate || '',
      hours: record.hours || '',
      rounds: record.rounds || '',
      placementOption: record.placementOption || ''
    });
    setShowPointModal(true);
  };

  const handleSubmitPoint = async (e) => {
    e.preventDefault();

    if (!selectedMember) return;

    const category = POINT_CATEGORIES.find(c => c.value === pointForm.category);
    let finalPoints = pointForm.points;

    // Calculate points based on hours or rounds
    if (category?.requiresHours && pointForm.hours) {
      const hours = parseFloat(pointForm.hours);
      if (isNaN(hours) || hours <= 0) {
        alert('Please enter a valid number of hours');
        return;
      }
      finalPoints = hours * category.defaultPoints;
    } else if (category?.perRound && pointForm.rounds) {
      const rounds = parseFloat(pointForm.rounds);
      if (isNaN(rounds) || rounds <= 0) {
        alert('Please enter a valid number of rounds');
        return;
      }
      finalPoints = rounds * category.defaultPoints;
    }

    // Save scroll position before updating
    if (leaderboardListRef.current) {
      scrollPositionRef.current = leaderboardListRef.current.scrollTop;
    }

    if (editingPoint) {
      // Update existing point record
      const result = await updatePointRecord(
        editingPoint.id,
        {
          ...pointForm,
          points: finalPoints,
          memberId: selectedMember.id,
          memberName: selectedMember.name
        },
        editingPoint.points,
        currentUser?.email || 'admin'
      );

      if (result.success) {
        setShowPointModal(false);
        await loadMembers();
        await loadMemberRecords(selectedMember.id);
      } else {
        alert('Error updating point record');
      }
    } else {
      // Create new point record
      const result = await createPointRecord({
        memberId: selectedMember.id,
        memberName: selectedMember.name,
        ...pointForm,
        points: finalPoints,
        createdBy: currentUser?.email || 'admin'
      });

      if (result.success) {
        setShowPointModal(false);
        await loadMembers();
        await loadMemberRecords(selectedMember.id);
      } else {
        alert('Error creating point record');
      }
    }
  };

  const handleDeletePoint = async (recordId, memberId, points) => {
    if (window.confirm('Are you sure you want to delete this point record?')) {
      // Save scroll position before updating
      if (leaderboardListRef.current) {
        scrollPositionRef.current = leaderboardListRef.current.scrollTop;
      }
      const result = await deletePointRecord(recordId, memberId, points);
      if (result.success) {
        await loadMembers();
        await loadMemberRecords(memberId);
      } else {
        alert('Error deleting point record');
      }
    }
  };

  // Point Applications
  const handleSubmitApplication = async (e) => {
    e.preventDefault();

    if (!applicationForm.memberName || !applicationForm.studentId) {
      alert('Please fill in all required fields');
      return;
    }

    const category = POINT_CATEGORIES.find(c => c.value === applicationForm.category);
    let calculatedPoints = category?.defaultPoints || 0;

    // Validate and calculate points based on category
    if (category?.requiresHours) {
      if (!applicationForm.hours || !applicationForm.eventName) {
        alert('Please enter hours worked and event name');
        return;
      }
      const hours = parseFloat(applicationForm.hours);
      if (isNaN(hours) || hours <= 0) {
        alert('Please enter a valid number of hours');
        return;
      }
      calculatedPoints = hours * category.defaultPoints;
    } else if (category?.perRound) {
      if (!applicationForm.rounds || !applicationForm.eventName) {
        alert('Please enter number of rounds and event name');
        return;
      }
      const rounds = parseFloat(applicationForm.rounds);
      if (isNaN(rounds) || rounds <= 0) {
        alert('Please enter a valid number of rounds');
        return;
      }
      calculatedPoints = rounds * category.defaultPoints;
    } else if (category?.options && category.options.length > 0) {
      if (!applicationForm.placementOption) {
        alert('Please select an option');
        return;
      }
      const selectedOption = category.options.find(opt => opt.label === applicationForm.placementOption);
      calculatedPoints = selectedOption?.points || category.defaultPoints;
    }

    const result = await createPointApplication({
      ...applicationForm,
      calculatedPoints
    });

    if (result.success) {
      setShowApplicationModal(false);
      setApplicationForm({
        memberName: '',
        studentId: '',
        category: 'tournament',
        notes: '',
        hours: '',
        rounds: '',
        placementOption: '',
        eventName: '',
        eventDate: ''
      });
      await loadApplications();
      alert('Application submitted successfully!');
    } else {
      alert('Error submitting application');
    }
  };

  const handleApproveApplication = async (app) => {
    // Try to find member by student ID first, then by name
    let member = app.studentId
      ? members.find(m => m.studentId === app.studentId)
      : null;

    if (!member) {
      member = members.find(m => m.name?.toLowerCase() === app.memberName.toLowerCase() ||
                                 `${m.firstName} ${m.lastName}`.toLowerCase() === app.memberName.toLowerCase());
    }

    if (!member) {
      alert('Member not found. Please create the member first or verify the student ID matches.');
      return;
    }

    // Use calculated points from application if available, otherwise use default
    const points = app.calculatedPoints || app.points || POINT_CATEGORIES.find(c => c.value === app.category)?.defaultPoints || 0;

    const result = await approvePointApplication(
      app.id,
      currentUser?.email || 'admin',
      member.id,
      points,
      app // Pass the full application data
    );

    if (result.success) {
      await loadApplications();
      await loadMembers();
      alert('Application approved!');
    } else {
      alert('Error approving application');
    }
  };

  const handleRejectApplication = async (appId) => {
    const reason = prompt('Reason for rejection (optional):');
    const result = await rejectPointApplication(appId, currentUser?.email || 'admin', reason || '');

    if (result.success) {
      await loadApplications();
      alert('Application rejected');
    } else {
      alert('Error rejecting application');
    }
  };

  const handleDeleteApplication = async (appId) => {
    if (window.confirm('Are you sure you want to delete this application?')) {
      const result = await deletePointApplication(appId);
      if (result.success) {
        await loadApplications();
      } else {
        alert('Error deleting application');
      }
    }
  };

  const canManagePoints = userPermissions?.canEdit || userPermissions?.isAdmin;
  const pendingApplications = applications.filter(a => a.status === 'pending');

  // Show all members for everyone (public access)
  const displayMembers = members;

  // Filter members based on search query
  const filteredMembers = displayMembers.filter(member => {
    if (!searchQuery.trim()) return true;

    const query = searchQuery.toLowerCase();
    const fullName = `${member.firstName || ''} ${member.lastName || ''}`.toLowerCase();
    const name = (member.name || '').toLowerCase();
    const email = (member.email || '').toLowerCase();
    const studentId = (member.studentId || '').toLowerCase();
    const category = (member.category || '').toLowerCase();

    return fullName.includes(query) ||
           name.includes(query) ||
           email.includes(query) ||
           studentId.includes(query) ||
           category.includes(query);
  });

  return (
    <div className="point-tracker">
      <div className="point-tracker-header">
        <div className="header-with-back">
          <button onClick={() => navigate('/')} className="back-button">
            ← Back to Dashboard
          </button>
          <h1>Point Tracker</h1>
        </div>
        <div className="header-actions">
          <button onClick={() => setShowApplicationModal(true)} className="apply-btn">
            Apply for Points
          </button>
        </div>
      </div>

      {canManagePoints && (
        <div className="admin-tabs">
          <button
            className={`tab ${activeTab === 'records' ? 'active' : ''}`}
            onClick={() => setActiveTab('records')}
          >
            Point Tracker
          </button>
          <button
            className={`tab ${activeTab === 'applications' ? 'active' : ''}`}
            onClick={() => setActiveTab('applications')}
          >
            Applications
            {pendingApplications.length > 0 && (
              <span className="badge">{pendingApplications.length}</span>
            )}
          </button>
        </div>
      )}

      {/* Show different layouts based on permissions */}
      {!canManagePoints ? (
        // View-only users: Show full-page leaderboard list in panel
        <div className="leaderboard-panel">
          <div className="panel-header">
            <h2>Point Tracker Leaderboard</h2>
          </div>

          <div className="search-bar-container">
            <input
              type="text"
              className="search-bar"
              placeholder="Search by name, email, ID, or category..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
            />
            {searchQuery && (
              <button className="clear-search" onClick={() => setSearchQuery('')}>
                ✕
              </button>
            )}
          </div>

          {loading ? (
            <p>Loading members...</p>
          ) : (
            <div className="leaderboard-list" ref={leaderboardListRef}>
              {filteredMembers.length === 0 ? (
                <div className="empty-state">
                  <p>{searchQuery ? 'No members found matching your search' : 'No members yet'}</p>
                </div>
              ) : (
                // Sort members by total points (highest to lowest)
                [...filteredMembers]
                  .sort((a, b) => (b.totalPoints || 0) - (a.totalPoints || 0))
                  .map((member, index) => (
                    <div
                      key={member.id}
                      className="leaderboard-item"
                      onClick={() => handleSelectMember(member)}
                    >
                      <div className="leaderboard-rank">#{index + 1}</div>
                      <div className="leaderboard-info">
                        <div className="leaderboard-name">
                          {member.firstName || member.name} {member.lastName || ''}
                        </div>
                        <div className="leaderboard-details">
                          {userPermissions?.isAdmin && member.studentId && <span>ID: {member.studentId}</span>}
                          {member.grade && <span>Grade: {member.grade}</span>}
                          {member.category && <span>{member.category}</span>}
                        </div>
                      </div>
                      <div className="leaderboard-points">{member.totalPoints || 0} pts</div>
                    </div>
                  ))
              )}
            </div>
          )}
        </div>
      ) : activeTab === 'records' ? (
        // Show full-page leaderboard for users with permissions too in panel
        <div className="leaderboard-panel">
          <div className="panel-header">
            <h2>Point Tracker Leaderboard</h2>
            {canManagePoints && (
              <div className="header-buttons">
                <button onClick={handleCleanupMembers} className="add-btn cleanup-btn" title="Delete members without points">
                  🧹 Cleanup
                </button>
                <button onClick={handleCsvUpload} className="add-btn csv-btn" title="Upload CSV file">
                  📄 CSV
                </button>
                <button onClick={handleAddMember} className="add-btn" title="Add new member">
                  + Add Member
                </button>
              </div>
            )}
          </div>

          <div className="search-bar-container">
            <input
              type="text"
              className="search-bar"
              placeholder="Search by name, email, ID, or category..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
            />
            {searchQuery && (
              <button className="clear-search" onClick={() => setSearchQuery('')}>
                ✕
              </button>
            )}
          </div>

          {loading ? (
            <p>Loading members...</p>
          ) : (
            <div className="leaderboard-list" ref={leaderboardListRef}>
              {filteredMembers.length === 0 ? (
                <div className="empty-state">
                  <p>{searchQuery ? 'No members found matching your search' : 'No members yet'}</p>
                </div>
              ) : (
                // Sort members by total points (highest to lowest)
                [...filteredMembers]
                  .sort((a, b) => (b.totalPoints || 0) - (a.totalPoints || 0))
                  .map((member, index) => (
                    <div
                      key={member.id}
                      className="leaderboard-item"
                      onClick={() => handleSelectMember(member)}
                    >
                      <div className="leaderboard-rank">#{index + 1}</div>
                      <div className="leaderboard-info">
                        <div className="leaderboard-name">
                          {member.firstName || member.name} {member.lastName || ''}
                        </div>
                        <div className="leaderboard-details">
                          {userPermissions?.isAdmin && member.studentId && <span>ID: {member.studentId}</span>}
                          {member.grade && <span>Grade: {member.grade}</span>}
                          {member.category && <span>{member.category}</span>}
                        </div>
                      </div>
                      <div className="leaderboard-points">{member.totalPoints || 0} pts</div>
                    </div>
                  ))
              )}
            </div>
          )}
        </div>
      ) : activeTab === 'members' ? (
        // This tab is no longer used but kept for compatibility
        <div className="tracker-content">
          <div className="members-panel">
            <div className="panel-header">
              <h2>Members</h2>
              <div className="header-buttons">
                <button onClick={handleCsvUpload} className="add-btn csv-btn" title="Upload CSV file">
                  📄 CSV
                </button>
                <button onClick={handleAddMember} className="add-btn" title="Add new member">
                  + Add
                </button>
              </div>
            </div>

            {loading ? (
              <p>Loading members...</p>
            ) : (
              <div className="members-list">
                {displayMembers.length === 0 ? (
                  <div className="empty-state">
                    <p>{canManagePoints ? 'No members yet' : 'No member record found for your account'}</p>
                  </div>
                ) : (
                  displayMembers.map(member => {
                    const isMrWilleby = currentUser?.email?.toLowerCase() === 'kaseywilleby@gmail.com' ||
                                        currentUser?.email?.toLowerCase() === 'mr.willeby@gmail.com';

                    return (
                      <div
                        key={member.id}
                        className={`member-item ${selectedMember?.id === member.id ? 'selected' : ''}`}
                        onClick={() => handleSelectMember(member)}
                      >
                        <div className="member-info">
                          <div
                            className={`member-name ${isMrWilleby ? 'clickable' : ''}`}
                            onClick={(e) => {
                              if (isMrWilleby) {
                                e.stopPropagation();
                                handleViewMemberDetails(member);
                              }
                            }}
                            title={isMrWilleby ? 'Click to view details' : ''}
                          >
                            {member.name}
                          </div>
                          <div className="member-meta">
                            {member.grade && <span className="member-grade">Grade {member.grade}</span>}
                            {member.category && <span className="member-category">{member.category}</span>}
                          </div>
                          <div className="member-points">{member.totalPoints || 0} points</div>
                        </div>
                        {canManagePoints && (
                          <div className="member-actions" onClick={(e) => e.stopPropagation()}>
                            <button onClick={() => handleAddPoints(member)} className="icon-btn" title="Add points">
                              +
                            </button>
                            <button onClick={() => handleEditMember(member)} className="icon-btn" title="Edit">
                              ✏️
                            </button>
                            <button onClick={() => handleDeleteMember(member.id)} className="icon-btn" title="Delete">
                              🗑️
                            </button>
                          </div>
                        )}
                      </div>
                    );
                  })
                )}
              </div>
            )}
          </div>

          <div className="records-panel">
            {selectedMember ? (
              <>
                <div className="panel-header">
                  <div>
                    <h2>{selectedMember.name}</h2>
                    <p className="total-points">{selectedMember.totalPoints || 0} Total Points</p>
                  </div>
                </div>

                <div className="records-list">
                  {memberRecords.length === 0 ? (
                    <div className="empty-state">
                      <p>No point records yet</p>
                    </div>
                  ) : (
                    memberRecords.map(record => (
                      <div key={record.id} className="record-item">
                        <div className="record-header">
                          <span className={`points-badge ${record.points < 0 ? 'negative' : 'positive'}`}>
                            {record.points > 0 ? '+' : ''}{record.points}
                          </span>
                          <span className="category-label">
                            {POINT_CATEGORIES.find(c => c.value === record.category)?.label || record.category}
                          </span>
                        </div>
                        {record.eventName && (
                          <div className="record-event">{record.eventName}</div>
                        )}
                        {record.notes && (
                          <div className="record-notes">{record.notes}</div>
                        )}
                        <div className="record-meta">
                          {record.eventDate && <span>Date: {record.eventDate}</span>}
                          <span>Created: {record.createdAt?.toDate?.()?.toLocaleDateString() || 'N/A'}</span>
                        </div>
                        {canManagePoints && (
                          <div className="record-actions">
                            <button onClick={() => handleEditPoint(record)} className="edit-btn-small">
                              Edit
                            </button>
                            {userPermissions?.isAdmin && (
                              <button onClick={() => handleDeletePoint(record.id, selectedMember.id, record.points)} className="delete-btn-small">
                                Delete
                              </button>
                            )}
                          </div>
                        )}
                      </div>
                    ))
                  )}
                </div>
              </>
            ) : (
              <div className="empty-state">
                <p>Select a member to view their point records</p>
              </div>
            )}
          </div>
        </div>
      ) : (
        <div className="applications-panel">
          <h2>Point Applications</h2>

          {applications.length === 0 ? (
            <div className="empty-state">
              <p>No applications yet</p>
            </div>
          ) : (
            <div className="applications-list">
              {applications.map(app => {
                const calculatedPoints = app.calculatedPoints || app.points || POINT_CATEGORIES.find(c => c.value === app.category)?.defaultPoints || 0;

                return (
                  <div key={app.id} className={`application-item ${app.status}`}>
                    <div className="application-header">
                      <div>
                        <strong>{app.memberName}</strong>
                        {app.studentId && <span className="student-id-badge">ID: {app.studentId}</span>}
                        <span className={`status-badge ${app.status}`}>{app.status}</span>
                        <span className="points-badge positive">+{calculatedPoints} pts</span>
                      </div>
                      <span className="category-label">
                        {POINT_CATEGORIES.find(c => c.value === app.category)?.label || app.category}
                      </span>
                    </div>

                    {app.eventName && (
                      <div className="application-detail">
                        <strong>Event:</strong> {app.eventName}
                      </div>
                    )}

                    {app.eventDate && (
                      <div className="application-detail">
                        <strong>Date:</strong> {app.eventDate}
                      </div>
                    )}

                    {app.hours && (
                      <div className="application-detail">
                        <strong>Hours:</strong> {app.hours} hours
                      </div>
                    )}

                    {app.rounds && (
                      <div className="application-detail">
                        <strong>Rounds:</strong> {app.rounds} rounds
                      </div>
                    )}

                    {app.placementOption && (
                      <div className="application-detail">
                        <strong>Placement:</strong> {app.placementOption}
                      </div>
                    )}

                    {app.notes && (
                      <div className="application-notes">
                        <strong>Notes:</strong> {app.notes}
                      </div>
                    )}

                    <div className="application-meta">
                      <span>Submitted: {app.createdAt?.toDate?.()?.toLocaleDateString() || 'N/A'}</span>
                    </div>

                    {app.status === 'pending' && canManagePoints && (
                      <div className="application-actions">
                        <button onClick={() => handleApproveApplication(app)} className="approve-btn">
                          Approve ({calculatedPoints} pts)
                        </button>
                        <button onClick={() => handleRejectApplication(app.id)} className="reject-btn">
                          Reject
                        </button>
                      </div>
                    )}

                    {app.status !== 'pending' && canManagePoints && (
                      <div className="application-actions">
                        <button onClick={() => handleDeleteApplication(app.id)} className="delete-btn-small">
                          Delete
                        </button>
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          )}
        </div>
      )}

      {/* Member Modal */}
      {showMemberModal && (
        <div className="modal-overlay" onClick={() => setShowMemberModal(false)}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <h2>{editingMember ? 'Edit Member' : 'Add Member'}</h2>
            <form onSubmit={handleSubmitMember}>
              <div className="form-row">
                <div className="form-group">
                  <label>First Name *</label>
                  <input
                    type="text"
                    value={memberForm.firstName}
                    onChange={(e) => setMemberForm({ ...memberForm, firstName: e.target.value })}
                    required
                  />
                </div>
                <div className="form-group">
                  <label>Last Name *</label>
                  <input
                    type="text"
                    value={memberForm.lastName}
                    onChange={(e) => setMemberForm({ ...memberForm, lastName: e.target.value })}
                    required
                  />
                </div>
              </div>
              <div className="form-row">
                <div className="form-group">
                  <label>Grade</label>
                  <input
                    type="text"
                    value={memberForm.grade}
                    onChange={(e) => setMemberForm({ ...memberForm, grade: e.target.value })}
                    placeholder="9, 10, 11, or 12"
                  />
                </div>
                <div className="form-group">
                  <label>Category</label>
                  <select
                    value={memberForm.category}
                    onChange={(e) => setMemberForm({ ...memberForm, category: e.target.value })}
                  >
                    <option value="">Select...</option>
                    <option value="Varsity">Varsity</option>
                    <option value="Novice">Novice</option>
                  </select>
                </div>
              </div>
              <div className="form-group">
                <label>Email</label>
                <input
                  type="email"
                  value={memberForm.email}
                  onChange={(e) => setMemberForm({ ...memberForm, email: e.target.value })}
                  placeholder="student@example.com"
                />
              </div>
              <div className="form-group">
                <label>Student ID</label>
                <input
                  type="text"
                  value={memberForm.studentId}
                  onChange={(e) => setMemberForm({ ...memberForm, studentId: e.target.value })}
                  placeholder="Student ID number"
                />
              </div>
              <div className="modal-actions">
                <button type="button" onClick={() => setShowMemberModal(false)} className="cancel-btn">
                  Cancel
                </button>
                <button type="submit" className="submit-btn">
                  {editingMember ? 'Update' : 'Add'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* CSV Upload Modal */}
      {showCsvUploadModal && (
        <div className="modal-overlay" onClick={() => setShowCsvUploadModal(false)}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <h2>Upload Members CSV</h2>
            <p className="modal-subtitle">Upload a CSV file with the following format:</p>
            <div className="csv-format-example">
              <code>First Name, Last Name, Grade, Varsity/Novice, Email, Student ID</code>
            </div>
            <form onSubmit={handleSubmitCsv}>
              <div className="form-group">
                <label>Select CSV File *</label>
                <input
                  type="file"
                  accept=".csv"
                  onChange={handleCsvFileChange}
                  required
                />
                {csvFile && <p className="file-selected">Selected: {csvFile.name}</p>}
              </div>

              {csvUploadStatus && (
                <div className={`upload-status ${csvUploadStatus.status}`}>
                  <p>{csvUploadStatus.message}</p>
                  {csvUploadStatus.errors && csvUploadStatus.errors.length > 0 && (
                    <div className="upload-errors">
                      <strong>Errors:</strong>
                      <ul>
                        {csvUploadStatus.errors.map((error, index) => (
                          <li key={index}>{error}</li>
                        ))}
                      </ul>
                    </div>
                  )}
                </div>
              )}

              <div className="modal-actions">
                <button type="button" onClick={() => setShowCsvUploadModal(false)} className="cancel-btn">
                  Cancel
                </button>
                <button type="submit" className="submit-btn" disabled={!csvFile || csvUploadStatus?.status === 'loading'}>
                  {csvUploadStatus?.status === 'loading' ? 'Uploading...' : 'Upload'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Member Detail Modal (Mr. Willeby Only) */}
      {showMemberDetailModal && selectedMemberDetails && (
        <div className="modal-overlay" onClick={() => setShowMemberDetailModal(false)}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <h2>Member Details</h2>
            <div className="member-detail-view">
              <div className="detail-row">
                <label>Name:</label>
                <span>{selectedMemberDetails.name}</span>
              </div>
              <div className="detail-row">
                <label>Grade:</label>
                <span>{selectedMemberDetails.grade || 'Not specified'}</span>
              </div>
              <div className="detail-row">
                <label>Category:</label>
                <span>{selectedMemberDetails.category || 'Not specified'}</span>
              </div>
              <div className="detail-row">
                <label>Total Points:</label>
                <span>{selectedMemberDetails.totalPoints || 0}</span>
              </div>
              <div className="detail-row sensitive">
                <label>Email:</label>
                <span>{selectedMemberDetails.email || 'Not provided'}</span>
              </div>
              <div className="detail-row sensitive">
                <label>Student ID:</label>
                <span>{selectedMemberDetails.studentId || 'Not provided'}</span>
              </div>
            </div>
            <div className="modal-actions">
              <button onClick={() => setShowMemberDetailModal(false)} className="submit-btn">
                Close
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Point History Modal */}
      {showPointHistoryModal && selectedMember && (
        <div className="modal-overlay" onClick={() => setShowPointHistoryModal(false)}>
          <div className="modal-content large-modal" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <div>
                <h3>{selectedMember.firstName || selectedMember.name} {selectedMember.lastName || ''}</h3>
                <p className="total-points-modal">{selectedMember.totalPoints || 0} Total Points</p>
              </div>
              <button onClick={() => setShowPointHistoryModal(false)} className="close-btn">×</button>
            </div>

            {/* Action Buttons for Users with Permissions */}
            {canManagePoints && (
              <div className="modal-actions">
                <button
                  onClick={() => {
                    setShowPointHistoryModal(false);
                    handleAddPoints(selectedMember);
                  }}
                  className="submit-btn"
                >
                  Add/Deduct Points
                </button>
                <button
                  onClick={() => {
                    setShowPointHistoryModal(false);
                    handleEditMember(selectedMember);
                  }}
                  className="cancel-btn"
                >
                  Edit Member
                </button>
              </div>
            )}

            <div className="modal-body">
              <div className="records-list">
                {memberRecords.length === 0 ? (
                  <div className="empty-state">
                    <p>No point records yet</p>
                  </div>
                ) : (
                  memberRecords.map(record => {
                    // Get initials from createdBy email
                    const createdByEmail = record.createdBy || 'admin';
                    const createdByName = createdByEmail.split('@')[0];
                    const initials = createdByName.split('.').map(n => n[0]?.toUpperCase()).join('') || 'A';

                    return (
                    <div key={record.id} className="record-item">
                      <div className="record-header-row">
                        <div className="record-header">
                          <span className={`points-badge ${record.points < 0 ? 'negative' : 'positive'}`}>
                            {record.points > 0 ? '+' : ''}{record.points} pts
                          </span>
                          <span className="category-label">
                            {POINT_CATEGORIES.find(c => c.value === record.category)?.label || record.category}
                          </span>
                        </div>
                        <div className="record-creator">
                          <div className="creator-avatar" title={`Entered by ${createdByEmail}`}>
                            {initials}
                          </div>
                        </div>
                      </div>
                      {record.eventName && (
                        <div className="record-event">
                          <strong>Event:</strong> {record.eventName}
                        </div>
                      )}
                      {record.hours && (
                        <div className="record-detail">
                          <strong>Hours:</strong> {record.hours} hours
                        </div>
                      )}
                      {record.rounds && (
                        <div className="record-detail">
                          <strong>Rounds:</strong> {record.rounds} rounds
                        </div>
                      )}
                      {record.placementOption && (
                        <div className="record-detail">
                          <strong>Placement:</strong> {record.placementOption}
                        </div>
                      )}
                      {record.notes && (
                        <div className="record-notes">
                          <strong>Notes:</strong> {record.notes}
                        </div>
                      )}
                      <div className="record-meta">
                        {record.eventDate && (
                          <span className="event-date">
                            <strong>Event Date:</strong> {record.eventDate}
                          </span>
                        )}
                        <span className="created-date">
                          <strong>Entered:</strong> {record.createdAt?.toDate?.()?.toLocaleDateString() || 'N/A'}
                        </span>
                        {record.lastEditedBy && (
                          <span className="edited-by">
                            <strong>Last edited by:</strong> {record.lastEditedBy}
                          </span>
                        )}
                      </div>
                      {canManagePoints && (
                        <div className="record-actions">
                          <button
                            onClick={() => {
                              setShowPointHistoryModal(false);
                              handleEditPoint(record);
                            }}
                            className="edit-btn-small"
                          >
                            Edit
                          </button>
                          {userPermissions?.isAdmin && (
                            <button
                              onClick={() => handleDeletePoint(record.id, selectedMember.id, record.points)}
                              className="delete-btn-small"
                            >
                              Delete
                            </button>
                          )}
                        </div>
                      )}
                    </div>
                    );
                  })
                )}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Point Modal */}
      {showPointModal && (() => {
        const selectedCategory = POINT_CATEGORIES.find(c => c.value === pointForm.category);
        const showHours = selectedCategory?.requiresHours;
        const showRounds = selectedCategory?.perRound;
        const showOptions = selectedCategory?.options && selectedCategory.options.length > 0;
        const isFixedPoints = selectedCategory?.fixedPoints;

        return (
          <div className="modal-overlay" onClick={() => setShowPointModal(false)}>
            <div className="modal-content" onClick={(e) => e.stopPropagation()}>
              <h2>{editingPoint ? 'Edit Point Record' : 'Add Points'}</h2>
              <form onSubmit={handleSubmitPoint}>
                <div className="form-group">
                  <label>Category *</label>
                  <select
                    value={pointForm.category}
                    onChange={(e) => handleCategoryChange(e.target.value)}
                    required
                  >
                    {POINT_CATEGORIES.map(cat => (
                      <option key={cat.value} value={cat.value}>{cat.label}</option>
                    ))}
                  </select>
                </div>

                {/* Hours input for volunteering */}
                {showHours && (
                  <div className="form-group">
                    <label>Hours Worked *</label>
                    <input
                      type="number"
                      step="0.5"
                      min="0"
                      value={pointForm.hours}
                      onChange={(e) => {
                        const hours = parseFloat(e.target.value) || 0;
                        setPointForm({
                          ...pointForm,
                          hours: e.target.value,
                          points: hours * selectedCategory.defaultPoints
                        });
                      }}
                      placeholder="Enter number of hours"
                      required
                    />
                    <small>Points: {pointForm.hours ? (parseFloat(pointForm.hours) * selectedCategory.defaultPoints).toFixed(0) : '0'} ({selectedCategory.defaultPoints} pts/hour)</small>
                  </div>
                )}

                {/* Rounds input for judging */}
                {showRounds && (
                  <div className="form-group">
                    <label>Number of Rounds *</label>
                    <input
                      type="number"
                      min="1"
                      value={pointForm.rounds}
                      onChange={(e) => {
                        const rounds = parseInt(e.target.value) || 0;
                        setPointForm({
                          ...pointForm,
                          rounds: e.target.value,
                          points: rounds * selectedCategory.defaultPoints
                        });
                      }}
                      placeholder="Enter number of rounds"
                      required
                    />
                    <small>Points: {pointForm.rounds ? (parseInt(pointForm.rounds) * selectedCategory.defaultPoints).toFixed(0) : '0'} ({selectedCategory.defaultPoints} pts/round)</small>
                  </div>
                )}

                {/* Options for categories with multiple point values */}
                {showOptions && (
                  <div className="form-group">
                    <label>Select Option *</label>
                    <select
                      value={pointForm.placementOption}
                      onChange={(e) => {
                        const selectedOption = selectedCategory.options.find(opt => opt.label === e.target.value);
                        setPointForm({
                          ...pointForm,
                          placementOption: e.target.value,
                          points: selectedOption?.points || selectedCategory.defaultPoints
                        });
                      }}
                      required
                    >
                      <option value="">Select...</option>
                      {selectedCategory.options.map(opt => (
                        <option key={opt.label} value={opt.label}>
                          {opt.label} - {opt.points} pts
                        </option>
                      ))}
                    </select>
                  </div>
                )}

                {/* Points field - read-only for fixed points, editable for others */}
                {!showHours && !showRounds && !showOptions && (
                  <div className="form-group">
                    <label>Points *</label>
                    <input
                      type="number"
                      value={pointForm.points}
                      onChange={(e) => setPointForm({ ...pointForm, points: parseInt(e.target.value) || 0 })}
                      readOnly={isFixedPoints}
                      required
                    />
                    {isFixedPoints && <small>Fixed point value for this category</small>}
                  </div>
                )}

                <div className="form-group">
                  <label>Event Name {(showHours || showRounds) ? '*' : ''}</label>
                  <input
                    type="text"
                    value={pointForm.eventName}
                    onChange={(e) => setPointForm({ ...pointForm, eventName: e.target.value })}
                    placeholder="e.g., Work Session, TFA Tournament, Debate 101"
                    required={showHours || showRounds}
                  />
                </div>

                <div className="form-group">
                  <label>Event Date</label>
                  <input
                    type="date"
                    value={pointForm.eventDate}
                    onChange={(e) => setPointForm({ ...pointForm, eventDate: e.target.value })}
                  />
                </div>

                <div className="form-group">
                  <label>Notes</label>
                  <textarea
                    value={pointForm.notes}
                    onChange={(e) => setPointForm({ ...pointForm, notes: e.target.value })}
                    placeholder="Additional notes..."
                    rows="3"
                  />
                </div>

                <div className="modal-actions">
                  <button type="button" onClick={() => setShowPointModal(false)} className="cancel-btn">
                    Cancel
                  </button>
                  <button type="submit" className="submit-btn">
                    {editingPoint ? 'Update' : 'Add'}
                  </button>
                </div>
              </form>
            </div>
          </div>
        );
      })()}

      {/* Application Modal */}
      {showApplicationModal && (() => {
        const selectedCategory = POINT_CATEGORIES.find(c => c.value === applicationForm.category);
        const showHours = selectedCategory?.requiresHours;
        const showRounds = selectedCategory?.perRound;
        const showOptions = selectedCategory?.options && selectedCategory.options.length > 0;

        return (
          <div className="modal-overlay" onClick={() => setShowApplicationModal(false)}>
            <div className="modal-content" onClick={(e) => e.stopPropagation()}>
              <h2>Apply for Points</h2>
              <p className="modal-subtitle">Submit your point application for approval</p>
              <form onSubmit={handleSubmitApplication}>
                <div className="form-group">
                  <label>Your Name *</label>
                  <input
                    type="text"
                    value={applicationForm.memberName}
                    onChange={(e) => setApplicationForm({ ...applicationForm, memberName: e.target.value })}
                    required
                  />
                </div>
                <div className="form-group">
                  <label>Student ID *</label>
                  <input
                    type="text"
                    value={applicationForm.studentId}
                    onChange={(e) => setApplicationForm({ ...applicationForm, studentId: e.target.value })}
                    placeholder="Enter your student ID number"
                    required
                  />
                </div>
                <div className="form-group">
                  <label>Category *</label>
                  <select
                    value={applicationForm.category}
                    onChange={(e) => {
                      const newCategory = POINT_CATEGORIES.find(c => c.value === e.target.value);
                      setApplicationForm({
                        ...applicationForm,
                        category: e.target.value,
                        hours: '',
                        rounds: '',
                        placementOption: '',
                        eventName: '',
                        eventDate: ''
                      });
                    }}
                    required
                  >
                    {POINT_CATEGORIES.filter(c => c.value !== 'deduction').map(cat => (
                      <option key={cat.value} value={cat.value}>{cat.label}</option>
                    ))}
                  </select>
                </div>

                {/* Event Name - required for hours/rounds categories */}
                {(showHours || showRounds) && (
                  <div className="form-group">
                    <label>Event Name *</label>
                    <input
                      type="text"
                      value={applicationForm.eventName}
                      onChange={(e) => setApplicationForm({ ...applicationForm, eventName: e.target.value })}
                      placeholder="e.g., Work Session, TFA Tournament"
                      required
                    />
                  </div>
                )}

                {/* Event Date - optional but recommended */}
                {(showHours || showRounds || showOptions) && (
                  <div className="form-group">
                    <label>Event Date {(showHours || showRounds) ? '*' : ''}</label>
                    <input
                      type="date"
                      value={applicationForm.eventDate}
                      onChange={(e) => setApplicationForm({ ...applicationForm, eventDate: e.target.value })}
                      required={showHours || showRounds}
                    />
                  </div>
                )}

                {/* Hours input for volunteering */}
                {showHours && (
                  <div className="form-group">
                    <label>Hours Worked *</label>
                    <input
                      type="number"
                      step="0.5"
                      min="0"
                      value={applicationForm.hours}
                      onChange={(e) => setApplicationForm({ ...applicationForm, hours: e.target.value })}
                      placeholder="Enter number of hours"
                      required
                    />
                    <small>Points will be calculated: {applicationForm.hours ? (parseFloat(applicationForm.hours) * selectedCategory.defaultPoints).toFixed(0) : '0'} points ({selectedCategory.defaultPoints} pts/hour)</small>
                  </div>
                )}

                {/* Rounds input for judging */}
                {showRounds && (
                  <div className="form-group">
                    <label>Number of Rounds *</label>
                    <input
                      type="number"
                      min="1"
                      value={applicationForm.rounds}
                      onChange={(e) => setApplicationForm({ ...applicationForm, rounds: e.target.value })}
                      placeholder="Enter number of rounds"
                      required
                    />
                    <small>Points will be calculated: {applicationForm.rounds ? (parseInt(applicationForm.rounds) * selectedCategory.defaultPoints).toFixed(0) : '0'} points ({selectedCategory.defaultPoints} pts/round)</small>
                  </div>
                )}

                {/* Options for categories with multiple point values */}
                {showOptions && (
                  <div className="form-group">
                    <label>Select Option *</label>
                    <select
                      value={applicationForm.placementOption}
                      onChange={(e) => setApplicationForm({ ...applicationForm, placementOption: e.target.value })}
                      required
                    >
                      <option value="">Select...</option>
                      {selectedCategory.options.map(opt => (
                        <option key={opt.label} value={opt.label}>
                          {opt.label} - {opt.points} pts
                        </option>
                      ))}
                    </select>
                  </div>
                )}

                <div className="form-group">
                  <label>Notes {!(showHours || showRounds) ? '*' : ''}</label>
                  <textarea
                    value={applicationForm.notes}
                    onChange={(e) => setApplicationForm({ ...applicationForm, notes: e.target.value })}
                    placeholder="Additional information or context"
                    rows="3"
                    required={!(showHours || showRounds)}
                  />
                  {!(showHours || showRounds) && (
                    <small>Please include event details and date</small>
                  )}
                </div>

                <div className="modal-actions">
                  <button type="button" onClick={() => setShowApplicationModal(false)} className="cancel-btn">
                    Cancel
                  </button>
                  <button type="submit" className="submit-btn">
                    Submit Application
                  </button>
                </div>
              </form>
            </div>
          </div>
        );
      })()}
    </div>
  );
}

export default PointTracker;
